import {
	DEFAULT_LINE_SYMBOL,
	DEFAULT_LINE_SYMBOL_COLOR,
	DEFAULT_LINE_SYMBOL_ID,
	IkeToken,
	LINE_SYMBOL_COLOR_PALETTE,
	resolveLineSymbol,
	STYLABLE_LINE_SYMBOL_SHAPES,
} from '@ikegps/core-api';
import { FormModelFieldLine } from '@ikegps/core-shared';
import { action, makeObservable, observable } from 'mobx';
import { ImageButton } from '../../../components/button/image.button';
import { Icon } from '../../../components/icon';
import { MaterialIcon } from '../../../components/icon/icons';
import { Input } from '../../../components/input/input';
import { type InputState } from '../../../components/input/input.state';
import { LineSwatch } from '../../../components/line.swatch';
import { ProFeatureTooltip } from '../../../components/pro.feature.tooltip';
import { SectionHeader } from '../../../components/section.header/section.header';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import { m, type MChild, type MithrilVNode } from '../../../mithril';
import { Services } from '../../../services';
import { classNames } from '../../../util/classnames';
import { AnchoredPopover } from '../../../components/anchored.popover/anchored.popover';
import { CSS as PopoverCSS } from '../../../components/anchored.popover/anchored.popover.style';
import { LineSymbolCustomizer } from '../../line.symbol.customizer/line.symbol.customizer';
import { FormDesigner } from '../form.designer';
import { FormFieldEditor } from './form.field.editor';

export const CSS = {
	container: 'c-FdcLine',
	rows: 'c-FdcLine__rows',
	header: 'c-FdcLine__header',
	section: 'c-FdcLine__section',
	rowGroup: 'c-FdcLine__rowGroup',
	row: 'c-FdcLine__row',
	text: 'c-FdcLine__text',
	textLabel: 'c-FdcLine__textLabel',
	symbol: 'c-FdcLine__symbol',
	symbolButton: 'c-FdcLine__symbolButton',
	symbolSlot: 'c-FdcLine__symbolSlot',
	symbolEdit: 'c-FdcLine__symbolEdit',
	visible: 'c-FdcLine__visible',
	visibleIcon: 'c-FdcLine__visibleIcon',
	actions: 'c-FdcLine__actions',
	hidden: 'c-FdcLine__hidden',
	ghost: 'c-FdcLine__ghost',
	error: 'c-FdcLine__error',
	disabled: 'c-FdcLine__disabled',
};

// symbol = pencil slot (16) + gap (4) + swatch (48) + gap (4) + spacer slot (16)
const COL = { symbol: '88px', visible: '40px', actions: '40px' };

// Sentinel marking the name's position in the localized heading, so we can split around it and only ellipsize the name.
const NAME_SLOT = '\u0000';

addStyles({
	[CSS.container]: {
		width: '100%',
		'max-width': '580px',
		display: 'flex',
		'flex-direction': 'column',
		padding: DefaultTheme.space(2),
	},
	// The variable-length row list is isolated from the popover sibling so inserting a row never shifts it.
	[CSS.rows]: { display: 'flex', 'flex-direction': 'column' },
	[CSS.header]: {
		display: 'flex',
		'align-items': 'center',
		gap: DefaultTheme.space(1),
		'font-weight': 'bold',
		color: DefaultTheme.font.color.supporting,
		'margin-bottom': DefaultTheme.space(1),
	},
	// Each section's rows live in their own container so list diffing stays isolated per section.
	[CSS.section]: { display: 'flex', 'flex-direction': 'column' },
	// Each row + its inline error are one group, so the divider sits below the error, not above it.
	[CSS.rowGroup]: { 'border-bottom': `1px solid ${DefaultTheme.font.color.divider}` },
	[CSS.row]: {
		display: 'flex',
		'align-items': 'center',
		gap: DefaultTheme.space(1),
		'min-height': '32px',
		padding: '2px 0',
	},
	// 32px cells (matching the select-list editor) keep preset labels and custom inputs on one rhythm.
	[CSS.text]: { flex: '1 1 auto', 'min-width': '120px', height: '32px', display: 'flex', 'align-items': 'center' },
	[CSS.textLabel]: { overflow: 'hidden', 'text-overflow': 'ellipsis', 'white-space': 'nowrap' },
	[CSS.symbol]: { width: COL.symbol, display: 'flex', 'align-items': 'center', 'justify-content': 'center' },
	[CSS.symbolButton]: {
		display: 'flex',
		'align-items': 'center',
		'justify-content': 'center',
		gap: '4px',
		width: '100%',
		cursor: 'pointer',
		background: 'none',
		border: 'none',
		'border-radius': '4px',
		padding: '4px 0',
	},
	[`${CSS.symbolButton}:hover`]: { background: '#d2d2d2' },
	// Fixed-width slots flank the swatch symmetrically so it shares the preset cells' centerline.
	[CSS.symbolSlot]: {
		flex: '0 0 16px',
		width: '16px',
		display: 'flex',
		'align-items': 'center',
		'justify-content': 'center',
	},
	[CSS.symbolEdit]: { 'font-size': '14px', color: DefaultTheme.font.color.supporting },
	[CSS.visible]: { width: COL.visible, display: 'flex', 'align-items': 'center', 'justify-content': 'center' },
	// Visible = accent orange, matching the customizer's active-chip accent; hidden falls back to the button's default grey.
	[CSS.visibleIcon]: { color: DefaultTheme.color.accent },
	[CSS.actions]: { width: COL.actions, display: 'flex', 'align-items': 'center', 'justify-content': 'center' },
	[CSS.hidden]: { opacity: 0.45 },
	[CSS.ghost]: { opacity: 0.55 },
	[CSS.error]: {
		color: DefaultTheme.font.color.error,
		'font-size': '12px',
		padding: `0 0 ${DefaultTheme.space(1)} ${DefaultTheme.space(1)}`,
	},
	[CSS.disabled]: { opacity: 0.5 },
});

export class FormFieldEditorLine extends FormFieldEditor<FormModelFieldLine> {
	/** A single shared customizer popover; opened against whichever row's symbol is clicked. */
	private readonly symbolPopover = new AnchoredPopover({ width: 320 });

	/** Section collapse flags. Observable: the app repaints via a MobX autorun, not auto-redraw. */
	@observable private isPresetsCollapsed = false;
	@observable private isCustomsCollapsed = false;

	constructor(designer: FormDesigner, field: FormModelFieldLine) {
		super(designer, field);
		makeObservable(this);
	}

	@action.bound private togglePresets() {
		this.isPresetsCollapsed = !this.isPresetsCollapsed;
	}

	@action.bound private toggleCustoms() {
		this.isCustomsCollapsed = !this.isCustomsCollapsed;
	}

	override destroy() {
		this.symbolPopover.close();
		super.destroy();
	}

	override viewInfoRW(): MithrilVNode {
		// Register the field-level validity gate so the tree node goes red when every type is hidden.
		this.visibilityState();
		// Without the line-tool token an existing field stays visible but read-only (no add row, all controls inert).
		const disabled = !Services.Token.has(IkeToken.FEATURE_LINE_TOOL);
		// Partition by isPresetOption rather than assuming presets are contiguous (a custom row duplicating a
		// preset title renders preset-like — accepted edge).
		const presetIndices: number[] = [];
		const customIndices: number[] = [];
		this.field.lineTypes.forEach((_lineType, index) => {
			(this.field.isPresetOption(index) ? presetIndices : customIndices).push(index);
		});

		const customRows = customIndices.map((index) => this.viewRow(index));
		// Editing the trailing row — its title or its symbol — is what adds it (like a select list).
		if (!disabled) {
			customRows.push(this.viewPlaceholderRow());
		}

		const body = m(
			'div',
			{
				className: classNames(CSS.container, { [CSS.disabled]: disabled }),
				// One lock for the whole subtree, rather than threading `disabled` through every input/button.
				style: disabled ? { pointerEvents: 'none' } : undefined,
			},
			[
				// Keep the popover a stable sibling of the row list; if it shared the list's children array its
				// open-state [backdrop, panel] nodes would interleave with row diffing and corrupt the placeholder.
				m('div', { className: CSS.rows }, [
					// Presets are visibility-toggle-only.
					SectionHeader.view({
						title: Translate(T.FIELD_LINE_DEFAULT_TYPES, { count: presetIndices.length }),
						collapsed: this.isPresetsCollapsed,
						onToggle: this.togglePresets,
					}),
					// Preset rows hold no input states, so collapsing can simply drop them.
					m(
						'div',
						{ className: CSS.section },
						this.isPresetsCollapsed
							? []
							: [this.viewColumnHeader(), ...presetIndices.map((index) => this.viewRow(index))]
					),
					// Customs trail the presets. Null keeps the children array a fixed shape (safe; only growing lists corrupt the diff).
					customRows.length === 0
						? null
						: SectionHeader.view({
								// Counts only real custom rows, not the trailing add placeholder.
								title: Translate(T.FIELD_LINE_CUSTOM_TYPES, { count: customIndices.length }),
								collapsed: this.isCustomsCollapsed,
								onToggle: this.toggleCustoms,
							}),
					m(
						'div',
						{ className: CSS.section },
						customRows.length === 0 || this.isCustomsCollapsed ? [] : [this.viewColumnHeader(), ...customRows]
					),
				]),
				// Stable wrapper: the popover fragment must never sit next to a conditionally-null sibling (skipped onRemove).
				m('div', {}, this.viewVisibilityError()),
				this.symbolPopover.view(),
			]
		);
		return disabled
			? ProFeatureTooltip.view({ stretch: true, message: Translate(T.FORM_OPTION_PRO_ONLY_TITLE), children: body })
			: body;
	}

	private viewColumnHeader(): MithrilVNode {
		return m('div', { className: CSS.header }, [
			m('div', { className: CSS.text }, Translate(T.FIELD_LINE_TYPE)),
			m('div', { className: CSS.symbol }, Translate(T.FIELD_LINE_SYMBOL)),
			m('div', { className: CSS.visible }, Translate(T.FIELD_LINE_VISIBLE)),
			m('div', { className: CSS.actions }),
		]);
	}

	private viewRow(index: number): MithrilVNode {
		const lineType = this.field.lineTypes[index]!;
		const symbol = resolveLineSymbol(lineType);
		// A row is a fixed preset iff it is the first occurrence of a default-library title. A later row that
		// duplicates a preset title is a custom row (kept editable + flagged), not a locked preset.
		const isCustom = !this.field.isPresetOption(index);

		const row = m('div', { className: lineType.visible ? CSS.row : `${CSS.row} ${CSS.hidden}` }, [
			m(
				'div',
				{ className: CSS.text },
				isCustom ? Input.view(this.rowTitleState(index, false)) : m('div', { className: CSS.textLabel }, lineType.title)
			),
			m(
				'div',
				{ className: CSS.symbol },
				isCustom ? this.viewSymbolButton(index, symbol) : LineSwatch.view({ symbol })
			),
			m(
				'div',
				{ className: CSS.visible },
				ImageButton.view(lineType.visible ? MaterialIcon.VISIBILITY : MaterialIcon.VISIBILITY_OFF, {
					title: T.FIELD_LINE_VISIBLE,
					className: lineType.visible ? CSS.visibleIcon : undefined,
					onclick: () => this.field.Mutator.toggleVisibility(index),
				})
			),
			m(
				'div',
				{ className: CSS.actions },
				isCustom ? ImageButton.view(MaterialIcon.DELETE, { onclick: () => this.removeRow(index) }) : null
			),
		]);

		// One group per row so each row's inline error sits directly beneath it (above the divider).
		return m('div', { className: CSS.rowGroup }, [row, this.viewRowError(index)]);
	}

	/**
	 * The trailing add row. Keyed at `lineTypes.length`, it mirrors the custom-row structure so that the
	 * first edit promotes it in place: typing a title or picking a symbol appends the row (see
	 * `commitTitle`/`applySymbol`), the shared input state keeps focus, and a fresh placeholder appears below.
	 */
	private viewPlaceholderRow(): MithrilVNode {
		const index = this.field.lineTypes.length;
		const symbol = resolveLineSymbol({ symbolId: DEFAULT_LINE_SYMBOL_ID, symbolColor: DEFAULT_LINE_SYMBOL_COLOR });
		const row = m('div', { className: CSS.row }, [
			m('div', { className: `${CSS.text} ${CSS.ghost}` }, Input.view(this.rowTitleState(index, true))),
			m('div', { className: `${CSS.symbol} ${CSS.ghost}` }, this.viewSymbolButton(index, symbol)),
			// No visibility toggle / delete until the row exists.
			m('div', { className: CSS.visible }),
			m('div', { className: CSS.actions }),
		]);
		// Same group shape as a real row so the first edit promotes it in place (DOM + focus carry over).
		return m('div', { className: CSS.rowGroup }, [row, null]);
	}

	private viewSymbolButton(index: number, symbol: ReturnType<typeof resolveLineSymbol>): MithrilVNode {
		return m(
			'button',
			{
				type: 'button',
				className: CSS.symbolButton,
				title: this.symbolButtonTitle(index),
				onclick: (event: MouseEvent) => this.openSymbolCustomizer(event, index),
			},
			[
				// Empty spacer mirrors the pencil slot so the swatch stays on the shared column centerline.
				m('span', { className: CSS.symbolSlot }),
				LineSwatch.view({ symbol }),
				m('span', { className: CSS.symbolSlot }, Icon.view(MaterialIcon.EDIT, { className: CSS.symbolEdit })),
			]
		);
	}

	private symbolButtonTitle(index: number): string {
		const title = this.field.lineTypes[index]?.title;
		return title ? Translate(T.FIELD_LINE_CUSTOMIZE_SYMBOL, { title }) : Translate(T.FIELD_LINE_ADD_CUSTOM);
	}

	/** Same heading, but with the line-type name in its own ellipsizing segment so a long name never hides the close button. */
	private symbolPopoverTitle(index: number): MChild | MChild[] {
		const title = this.field.lineTypes[index]?.title;
		if (!title) {
			return Translate(T.FIELD_LINE_ADD_CUSTOM);
		}
		const [prefix = '', suffix = ''] = Translate(T.FIELD_LINE_CUSTOMIZE_SYMBOL, { title: NAME_SLOT }).split(NAME_SLOT);
		return [
			m('span', { className: PopoverCSS.titleFixed }, prefix),
			m('span', { className: PopoverCSS.titleName }, title),
			m('span', { className: PopoverCSS.titleFixed }, suffix),
		];
	}

	private openSymbolCustomizer(event: MouseEvent, index: number) {
		const anchor = event.currentTarget as HTMLElement;
		this.symbolPopover.open(anchor, {
			title: () => this.symbolPopoverTitle(index),
			content: () => this.viewSymbolCustomizer(index),
		});
	}

	private viewSymbolCustomizer(index: number): MithrilVNode {
		const lineType = this.field.lineTypes[index];
		const symbol = (lineType != null ? resolveLineSymbol(lineType) : null) ?? DEFAULT_LINE_SYMBOL;
		// Preserve the OTHER half from the stored row (or the defaults for the not-yet-added placeholder) so a
		// pick never silently rewrites an unresolvable symbolId. Live apply; the form's Save/Discard governs.
		const currentSymbolId = lineType?.symbolId ?? DEFAULT_LINE_SYMBOL_ID;
		const currentColor = lineType?.symbolColor ?? DEFAULT_LINE_SYMBOL_COLOR;
		return LineSymbolCustomizer.view({
			symbol,
			shapes: STYLABLE_LINE_SYMBOL_SHAPES,
			palette: LINE_SYMBOL_COLOR_PALETTE,
			onPickShape: (symbolId) => this.applySymbol(index, symbolId, currentColor),
			onPickColor: (color) => this.applySymbol(index, currentSymbolId, color),
		});
	}

	private viewRowError(index: number): MithrilVNode {
		const lineType = this.field.lineTypes[index];
		// Genuine presets always have a valid library title; the placeholder isn't a real row yet. A custom row
		// that duplicates a preset title is NOT a preset here, so its duplicate error must still show.
		if (lineType == null || this.field.isPresetOption(index)) {
			return null;
		}
		const title = lineType.title.trim();
		let message: T | null = null;
		if (title.length === 0) {
			message = T.FIELD_LINE_TYPE_REQUIRED;
		} else if (this.isDuplicateTitle(index, title)) {
			message = T.FIELD_LINE_TYPE_UNIQUE;
		}
		return message == null ? null : m('div', { className: CSS.error }, Translate(message));
	}

	private isDuplicateTitle(index: number, title: string): boolean {
		const target = title.toLowerCase();
		return this.field.lineTypes.some((other, i) => i !== index && other.title.trim().toLowerCase() === target);
	}

	/** A field with every type hidden offers nothing to draw — invalid (collected lines reference a visible type). */
	private hasNoVisibleType(): boolean {
		return this.field.visibleLineTypes.length === 0;
	}

	private viewVisibilityError(): MithrilVNode {
		return this.hasNoVisibleType()
			? m('div', { className: CSS.error }, Translate(T.FIELD_LINE_VISIBLE_REQUIRED))
			: null;
	}

	/**
	 * Field-level validity gate, mirroring the per-row title states: its error feeds `state.isValid`, which the
	 * tree view reads to turn this field's node red. The visible message is `viewVisibilityError`, so the state's
	 * own error is suppressed. Created lazily (and re-created after `clearStates`) so the reaction stays bound.
	 */
	private visibilityState(): InputState<string> {
		const key = 'visibility';
		let state = this.state.get(key);
		if (state == null) {
			state = this.state.child(T.FIELD_LINE_TYPE, key);
			state.isErrorShown = false;
			state.reaction(
				() => this.hasNoVisibleType(),
				(invalid) => state.setError(invalid ? Translate(T.FIELD_LINE_VISIBLE_REQUIRED) : ''),
				{ fireImmediately: true }
			);
		}
		return state;
	}

	private commitTitle(index: number, title: string) {
		if (index >= this.field.lineTypes.length) {
			this.field.Mutator.option({
				symbolId: DEFAULT_LINE_SYMBOL_ID,
				symbolColor: DEFAULT_LINE_SYMBOL_COLOR,
				title,
				visible: true,
			});
		} else {
			this.field.Mutator.setTitle(index, title);
		}
	}

	private applySymbol(index: number, symbolId: string, symbolColor: string) {
		if (index >= this.field.lineTypes.length) {
			this.field.Mutator.option({ symbolId, symbolColor, title: '', visible: true });
		} else {
			this.field.Mutator.setSymbol(index, symbolId, symbolColor);
		}
	}

	private removeRow(index: number) {
		this.field.Mutator.remove(index);
		// Row indices shift, so rebuild the per-row input states.
		this.state.clearStates();
	}

	/**
	 * One title input state per row, keyed by index so the placeholder (`index === lineTypes.length`) becomes
	 * the new row's state on the next render — value and focus carry over. `isRequired` is recomputed each
	 * render: false while a placeholder (so it never blocks save), true once it is a real row.
	 * React-port note: index-as-key bleeds row state on reorder/delete; the port needs a stable per-row key
	 * (line types carry no id today — title is their identity).
	 */
	private rowTitleState(index: number, isPlaceholder: boolean): InputState<string> {
		const key = `title:${index}`;
		let state = this.state.get(key);
		if (state == null) {
			state = this.state.child(T.FIELD_LINE_TYPE, key);
			// Suppress the input's own error; the inline `viewRowError` beneath the row is the visible feedback.
			state.isErrorShown = false;
			state.onValue((val: string) => this.commitTitle(index, val ?? ''));
			// Don't seed the placeholder's value — that would fire onValue and append an empty row on render.
			if (!isPlaceholder) {
				state.setValue(this.field.lineTypes[index]?.title ?? '');
			}
			state.reaction(
				() => this.titleError(index),
				(error) => state.setError(error),
				{ fireImmediately: true }
			);
		}
		state.isRequired = !isPlaceholder;
		state.setPlaceholder(isPlaceholder ? Translate(T.FIELD_LINE_TYPE_ADD) : '');
		return state;
	}

	/**
	 * Drives the row input's `error` (and thus save gating): a duplicate (non-empty) title invalidates the
	 * row. Empties are gated by `isRequired`. The visible message is the inline `viewRowError` beneath the row.
	 */
	private titleError(index: number): string {
		const current = this.field.lineTypes[index];
		const title = current?.title.trim() ?? '';
		if (title.length === 0) {
			return '';
		}
		return this.isDuplicateTitle(index, title) ? Translate(T.FIELD_LINE_TYPE_UNIQUE) : '';
	}
}
